// Prompt 0470 M8 — Tab Datos del panel de Tipo de Rol de Gobernanza.
// Edita el catalogo (nombre, descripcion, activo). Tipos de sistema son
// inmutables: nombre readonly, toggle activo bloqueado (siempre activo).

import type React from 'react';
import { useState, useEffect } from 'react';
import {
  actualizarTipoRolGobernanza,
  type TipoRolGobernanzaAdmin,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';

interface DatosTabProps {
  tipo: TipoRolGobernanzaAdmin | null;
  isDirty: boolean;
  onMarkDirty: () => void;
  onMarkClean: () => void;
  onSaved: (saved: TipoRolGobernanzaAdmin) => Promise<void> | void;
}

interface Form {
  nombre: string;
  descripcion: string;
  activo: boolean;
}

function buildForm(tipo: TipoRolGobernanzaAdmin | null): Form {
  return {
    nombre: tipo?.nombre ?? '',
    descripcion: tipo?.descripcion ?? '',
    activo: tipo?.activo ?? true,
  };
}

export function DatosTab({ tipo, isDirty, onMarkDirty, onMarkClean, onSaved }: DatosTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = ((adm ?? {}) as Record<string, unknown>).tipoRolGobernanzaPanel as
    | Record<string, string>
    | undefined;
  const panel = lbl ?? {};

  const esSistema = tipo?.esSistema ?? false;

  const [form, setForm] = useState<Form>(() => buildForm(tipo));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(buildForm(tipo));
  }, [tipo]);

  const updateField = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    onMarkDirty();
  };

  const handleSave = async () => {
    if (!tipo) {
      setError(panel.creacionDesdeCatalogo ?? 'Los tipos nuevos se crean desde el catalogo');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await actualizarTipoRolGobernanza(tipo.id, {
        nombre: esSistema ? undefined : form.nombre,
        descripcion: form.descripcion || null,
        activo: form.activo,
      });
      onMarkClean();
      await onSaved(res.data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const ambitoText = tipo?.esPorProceso
    ? (panel.ambitoPorProceso ?? 'Por proceso (se gestiona desde la ficha del proceso)')
    : (panel.ambitoGlobal ?? 'Global');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {esSistema && (
        <div
          style={{
            padding: '10px 14px',
            background: '#EEF2FF',
            color: '#4338CA',
            borderRadius: 8,
            fontSize: 12,
            border: '1px solid #C7D2FE',
          }}
        >
          {panel.avisoSistema ??
            'Este tipo de rol es del sistema: el nombre no se puede cambiar y las asignaciones se gestionan desde la ficha del proceso.'}
        </div>
      )}

      <Field label={panel.codigo ?? 'Codigo'}>
        <input
          type="text"
          value={tipo?.codigo ?? ''}
          readOnly
          style={{ ...inputStyle, background: '#F3F4F6', color: '#6B7280' }}
        />
      </Field>

      <Field label={panel.nombre ?? 'Nombre'} required>
        <input
          type="text"
          value={form.nombre}
          onChange={(e) => updateField('nombre', e.target.value)}
          readOnly={esSistema}
          maxLength={200}
          style={{
            ...inputStyle,
            ...(esSistema ? { background: '#F3F4F6', color: '#6B7280' } : {}),
          }}
        />
      </Field>

      <Field label={panel.descripcion ?? 'Descripcion'}>
        <textarea
          value={form.descripcion}
          onChange={(e) => updateField('descripcion', e.target.value)}
          maxLength={2000}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>

      <Field label={panel.ambito ?? 'Ambito'}>
        <input
          type="text"
          value={ambitoText}
          readOnly
          style={{ ...inputStyle, background: '#F3F4F6', color: '#6B7280' }}
        />
      </Field>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
          {panel.activo ?? 'Activo'}
        </label>
        <input
          type="checkbox"
          checked={form.activo}
          onChange={(e) => updateField('activo', e.target.checked)}
          disabled={esSistema && form.activo}
        />
        {esSistema && (
          <span style={{ fontSize: 11, color: '#6B7280' }}>
            {panel.sistemaSiempreActivo ?? '(Los tipos de sistema no se pueden desactivar)'}
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: '10px 14px',
            background: '#FEF2F2',
            color: '#B91C1C',
            borderRadius: 6,
            fontSize: 12,
            border: '1px solid #FECACA',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !tipo || (!isDirty && !esSistema)}
          style={{
            padding: '8px 20px',
            background: saving || !tipo || (!isDirty && !esSistema) ? '#9CA3AF' : '#1F3864',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: saving || !tipo ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {saving ? '...' : (panel.guardar ?? 'Guardar')}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 600,
          color: '#374151',
          marginBottom: 4,
        }}
      >
        {label} {required && <span style={{ color: '#DC2626' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
